import { useMemo, type FC, useEffect, memo, type PropsWithChildren, useCallback } from "react";
import { FullPageLoader } from "../components/FullPageLoader";
import { FullPageError } from "../components/FullPageError";
import { StyleEditor } from "@mapka/style-editor";
import { useTRPC } from "../trpc";
import { useAllParams, useBasePath } from "../hooks/router";
import { useIntl } from "react-intl";
import { useSetUser, useUser } from "../hooks/user";
import { useSetStyleEditorView, useStyleEditorView } from "../hooks/style";
import { useLocale, useSetLocale } from "../hooks/locale";
import { captureEvent } from "../utils/posthog";
import { useNavigateUserLayout } from "../routes/auth/hooks/navigate";
import { useDefaultApiKey, useSetDefaultApiKey } from "../hooks/apiKey";
import { useQuery } from "@tanstack/react-query";
import type { Locale } from "@project/common";
import type { StyleAndVersions, View } from "@mapka/style-editor";
import type { Chat } from "@project/api";
import { useNavigate } from "@tanstack/react-router";

const getStyleFromMessages = ([chat]: Chat[] = [], accountName = ""): StyleAndVersions | undefined => {
  const styleMessage = chat?.messages
    .filter(message => message.role === "assistant" || message.role === "user")
    .find(({ spec }) => typeof spec === "object");

  const spec = styleMessage?.spec;
  if (!spec) {
    return;
  }

  return {
    account: {
      name: accountName,
      displayName: accountName,
    },
    style: {
      id: 0,
      key: "inspect-style",
      displayName: "inspect-style",
      scope: "private",
      publishedVersionId: 1,
      currentVersionId: 1,
    },
    versions: [{
      id: 1,
      spec,
      createdAt: new Date().toISOString(),
    }],
  };
};

export const MapInspectDashboardLayout: FC<PropsWithChildren> = memo(({ children }) => {
  const user = useUser();
  const setUser = useSetUser();
  const editorView = useStyleEditorView();
  const navigate = useNavigateUserLayout();
  const setLocale = useSetLocale();
  const setStyleEditorView = useSetStyleEditorView();
  const setApiKey = useSetDefaultApiKey();
  const intl = useIntl();
  const trpc = useTRPC();
  const locale = useLocale();
  const routerNavigate = useNavigate();
  const apiKey = useDefaultApiKey();
  const pathname = useBasePath();

  const {
    accountName = "",
    map: styleKey = "",
    chatId,
  } = useAllParams();

  const {
    data: userData,
    isLoading,
    isError: isUserError,
    error: userError,
  } = useQuery(trpc.getCurrentUser.queryOptions(undefined, {
    enabled: !user,
  }));

  const { data: apiKeyData } = useQuery(trpc.getDefaultApiKey.queryOptions(undefined, {
    enabled: Boolean(user),
    refetchInterval: 1000 * 60 * 20,
    refetchOnMount: false,
  }));
  const {
    data: styleAndVersions,
    isLoading: isLoadingStyle,
    isError: isErrorStyle,
    error: styleError,
  } = useQuery(trpc.getInspectStyleWithVersions.queryOptions({
    key: styleKey,
    styleAccountName: accountName,
  }, {
    enabled: Boolean(user) && Boolean(styleKey),
  }));

  const { data: chats } = useQuery(trpc.inspectStyleEditorChat.queryOptions({
    chatId: Number(chatId),
  }, {
    enabled: Boolean(user) && Boolean(chatId),
    refetchInterval: 2000,
  }));

  const messageStyle = useMemo(() => {
    if (!chatId) {
      return;
    }
    return getStyleFromMessages(
      chats as unknown as Chat[] | undefined,
      accountName,
    );
  }, [accountName, chats, chatId]);

  useEffect(() => {
    if (userData) {
      if (userData.locale !== intl.locale) {
        setLocale(userData.locale as Locale);
      }
    }
  }, [userData, intl, setLocale]);

  useEffect(() => {
    if (userData) {
      setUser(userData);
      navigate(userData);
    }
  }, [userData, setUser, navigate, pathname]);

  useEffect(() => {
    if (apiKeyData) {
      setApiKey(apiKeyData);
    }
  }, [apiKeyData]);

  const onViewChange = useCallback((view: View) => {
    setStyleEditorView(view);
    void routerNavigate({
      to: `/$accountName/maps/$map/${view}`,
      params: { map: styleKey, accountName },
    });
  }, [routerNavigate, styleKey, accountName, setStyleEditorView]);

  const currentStyle = messageStyle ?? styleAndVersions;

  if (isLoading || isLoadingStyle) {
    return <FullPageLoader />;
  }
  if (isUserError) {
    return <FullPageError error={userError} />;
  }
  if (isErrorStyle) {
    return <FullPageError error={styleError} />;
  }
  if (!user || !currentStyle || !apiKey) {
    return <FullPageLoader />;
  }

  // tRPC-serialized output is not assignable to the raw StyleAndVersions the
  // editor expects (functions stripped, `unknown` widened); runtime shape matches.
  const style = currentStyle as unknown as StyleAndVersions;

  return (
    <>
      {children}
      <StyleEditor
        style={style}
        mapkaApiKey={apiKey}
        view={editorView}
        locale={locale}
        chatId={Number(chatId)}
        onViewChange={onViewChange}
        captureEvent={captureEvent}
        mode="inspect"
      />
    </>
  );
});
